import { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { ConfigContext } from '../../context';
import {
  IconLabel,
  USER_DIRECTORY_READER_COOKIE_KEY,
  useAppDispatch,
  useAppSelector,
} from '../../shared';
import { getOrganisationUsers, loadMoreOrganisationUsers } from '../../slices';
import { UserDirectoryItem } from './UserDirectoryItem';

import classes from './UserDirectory.module.scss';

export const UserDirectory: FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { localiseKey, filters: filtersConfig } =
    useContext(ConfigContext).config.elements.userDirectory;

  const [cookies] = useCookies([USER_DIRECTORY_READER_COOKIE_KEY]);

  const {
    requestFilter = {
      query: '',
      genders: [],
      filterPositions: [],
      filterDepartments: [],
      filterSubscriptions: [],
      organisationTagsId: [],
      rolesId: [],
      queryLocation: '',
    },
  } = { ...cookies[USER_DIRECTORY_READER_COOKIE_KEY] };

  const {
    name: { visible: nameVisible },
    gender: { visible: genderVisible },
    position: { visible: positionVisible },
    department: { visible: departmentVisible },
    subscription: { visible: subscriptionVisible },
    organisationTags: { visible: organisationTagsVisible },
    role: { visible: roleVisible },
    location: { visible: locationVisible },
  } = filtersConfig;

  const filters = useMemo(() => {
    return {
      ...requestFilter,
      ...(!nameVisible && { query: '' }),
      ...(!genderVisible && { genders: [] }),
      ...(!positionVisible && { filterPositions: [] }),
      ...(!departmentVisible && { filterDepartments: [] }),
      ...(!subscriptionVisible && { filterSubscriptions: [] }),
      ...(!organisationTagsVisible && { organisationTagsId: [] }),
      ...(!roleVisible && { rolesId: [] }),
      ...(!locationVisible && { queryLocation: '' }),
      excludeBanned: true,
    };
  }, [
    departmentVisible,
    genderVisible,
    locationVisible,
    nameVisible,
    organisationTagsVisible,
    positionVisible,
    requestFilter,
    roleVisible,
    subscriptionVisible,
  ]);

  const {
    isFetched,
    users,
    pageInfo: { page, hasNextPage },
  } = useAppSelector(({ organisationUsers }) => organisationUsers);

  useEffect(() => {
    dispatch(getOrganisationUsers({ filters }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const loadMore = useCallback(() => {
    dispatch(loadMoreOrganisationUsers({ page: page + 1, filters }));
  }, [dispatch, filters, page]);

  const loader = useMemo(() => {
    return (
      <div className={classes['user-directory__loader']}>
        <Skeleton height={64} width={'100%'} />
        <Skeleton height={64} width={'100%'} />
        <Skeleton height={64} width={'100%'} />
      </div>
    );
  }, []);

  const hasUsers = useMemo(() => Boolean(users.length), [users.length]);

  const content = useMemo(() => {
    if (!isFetched) {
      return loader;
    }

    if (!hasUsers) {
      return (
        <div className={classes['user-directory__empty']}>
          <IconLabel iconId={'search-off'} iconSize={48} nonClickable />
          <div className={classes['user-directory__empty-message']}>
            {t('userDirectory.empty-message')}
          </div>
        </div>
      );
    }
    return (
      <InfiniteScroll
        next={loadMore}
        hasMore={hasNextPage}
        loader={loader}
        dataLength={users.length}
        className={classes['user-directory__feed']}
      >
        {users.map((user) => (
          <UserDirectoryItem key={user.id} user={user} />
        ))}
      </InfiniteScroll>
    );
  }, [hasNextPage, hasUsers, isFetched, loadMore, loader, t, users]);

  return (
    <div className={classes['user-directory']}>
      <h1 className={classes['user-directory__title']}>{t(localiseKey)}</h1>
      {content}
    </div>
  );
};
